// Phase 12: the planning tabs, for a real couple.
//   Vendors (booked, money, paid), Budget (vendor costs + her non-vendor costs), Timeline (milestones from a
//   package template, the wedding-day run of show), the Weekly Report built from the week's real activity,
//   and the checks for files couples upload from their portal.
// Shared by the dashboard and the edge functions (Deno). Pure functions only.
import { ymd, addDays, daysBetween } from "./fees.js";
import { normalizeSettings } from "./packages.js";

const money = n => Math.round(Number(n || 0) * 100) / 100;
const num = v => { const n = typeof v === "string" ? Number(v.replace(/[$,\s]/g, "")) : Number(v); return Number.isFinite(n) ? n : 0; };
const str = (v, max = 500) => String(v == null ? "" : v).trim().slice(0, max);

/* ───────────── Vendors ───────────── */

// Stored contract statuses stay as they always were; the screens show the plain-English label.
export const VENDOR_STATUSES = ["Considering", "Pending", "Signed", "Rejected"];
export const VENDOR_STATUS_LABEL = { Considering: "Considering", Pending: "Contract out", Signed: "Booked", Rejected: "Not going ahead" };
export const vendorStatusLabel = s => VENDOR_STATUS_LABEL[s] || s || "Considering";
/** Booked or with a contract out: counts toward the budget and shows in the couple's portal. */
export const isCommitted = v => !!v && v.contractStatus !== "Rejected" && v.contractStatus !== "Considering";

/** A new, blank vendor for one couple (the same shape every vendor has always had). */
export function blankVendor(clientId, fields = {}, now = Date.now()) {
  const name = str(fields.name, 120);
  return {
    id: clientId + "-vendor-" + now.toString(36) + Math.random().toString(36).slice(2, 5), client: clientId,
    inv: name.slice(0, 2).toUpperCase(), name, category: str(fields.category, 60) || "Venue",
    contact: str(fields.contact, 120), phone: str(fields.phone, 40), email: str(fields.email, 160), website: str(fields.website, 300), address: str(fields.address, 300),
    contractStatus: VENDOR_STATUSES.includes(fields.contractStatus) ? fields.contractStatus : "Considering",
    range: "", keyTerms: str(fields.keyTerms, 1000),
    quote: money(num(fields.quote)), deposit: 0, depositDue: "", depositPaid: false, final: 0, finalDue: "", finalPaid: false, method: "",
    services: Array.isArray(fields.services) ? fields.services : String(fields.services || "").split(",").map(s => s.trim()).filter(Boolean),
    addons: [], lastContact: "", recent: false, notes: str(fields.notes, 2000), contacts: []
  };
}

/** Days are stored the way the vendor screens always showed them ("Mar 15, 2026"); "" or "—" means not set. */
const dayOf = v => (v && v !== "—" ? ymd(v) : null);

/** The vendor's two payments (retainer/deposit and final), each with a status on `today`. */
export function vendorPayments(vendors, today) {
  const out = [];
  (vendors || []).filter(isCommitted).forEach(v => {
    const st = (paid, due) => paid ? "Paid" : (due && due < today ? "Overdue" : "Pending");
    [["deposit", "Deposit", v.deposit, v.depositDue, v.depositPaid, v.depositPaidAt], ["final", "Final", v.final, v.finalDue, v.finalPaid, v.finalPaidAt]].forEach(([kind, label, amount, dueRaw, paid, paidAt]) => {
      if (!(num(amount) > 0)) return;
      const due = dayOf(dueRaw);
      out.push({ id: v.id + ":" + kind, vendorId: v.id, kind, vendor: v.name, label: label + " · " + v.name, due, dueText: dueRaw && dueRaw !== "—" ? dueRaw : "", amount: money(amount),
        method: v.method && v.method !== "—" ? v.method : "", invoice: v.inv ? v.inv + (kind === "deposit" ? "-201" : "-202") : "", status: st(!!paid, due), paid: !!paid, paidAt: paidAt || null });
    });
  });
  return out.sort((a, b) => String(a.due || "9999").localeCompare(String(b.due || "9999")));
}

/** Phase 13 · Step 3: one vendor's payment state for the Vendors list, in this order. */
export const VENDOR_PAY_STATUSES = ["Not booked", "Pending", "Deposit paid", "Fully paid", "Overdue"];
export const vendorPayRank = status => { const i = VENDOR_PAY_STATUSES.indexOf(status); return i < 0 ? -1 : i; };
export function vendorPayStatus(v, today) {
  if (!isCommitted(v)) return "Not booked";
  const p = vendorPayments([v], today);
  if (!p.length) return "Pending";
  if (p.some(x => x.status === "Overdue")) return "Overdue";
  if (p.every(x => x.paid)) return "Fully paid";
  if (p.some(x => x.kind === "deposit" && x.paid)) return "Deposit paid";
  return "Pending";
}

/** Adds a logged contact (newest first) and keeps lastContact in step with it. */
export function logContact(vendor, { note, at, by }) {
  const when = at || new Date().toISOString();
  const entry = { at: when, note: str(note, 1000), by: str(by, 120) };
  const contacts = [entry, ...(Array.isArray(vendor.contacts) ? vendor.contacts : [])].slice(0, 200);
  const d = new Date(when);
  return { ...vendor, contacts, lastContact: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), recent: true };
}

/* ───────────── Budget ───────────── */

/** A non-vendor cost (attire, rings, favors…). */
export function blankExpense(fields = {}, now = Date.now()) {
  return { id: "exp-" + now.toString(36) + Math.random().toString(36).slice(2, 5), label: str(fields.label, 160), category: str(fields.category, 60),
    amount: money(num(fields.amount)), due: ymd(fields.due) || "", paid: !!fields.paid, paidAt: fields.paid ? (fields.paidAt || new Date(now).toISOString()) : null, note: str(fields.note, 500) };
}

/**
 * Everything the Budget tab and the couple's Budget show. Booked vendors' quotes plus her non-vendor costs are
 * "committed"; paid counts vendor payments marked paid plus non-vendor costs marked paid.
 * The total budget may be 0 (not set yet): percentages are then null instead of dividing by zero.
 */
export function budgetSummary({ client, vendors, expenses, today }) {
  const vs = (vendors || []).filter(v => v && v.client === (client && client.id));
  const active = vs.filter(isCommitted);
  const exp = (expenses || []).filter(Boolean);
  const vendorTotal = money(active.reduce((a, v) => a + num(v.quote), 0));
  const expTotal = money(exp.reduce((a, e) => a + num(e.amount), 0));
  const committed = money(vendorTotal + expTotal);
  const payments = vendorPayments(vs, today);
  const paid = money(payments.filter(p => p.paid).reduce((a, p) => a + p.amount, 0) + exp.filter(e => e.paid).reduce((a, e) => a + num(e.amount), 0));
  const budget = num(client && client.budget);
  const byCat = {};
  active.forEach(v => { byCat[v.category || "Other"] = money((byCat[v.category || "Other"] || 0) + num(v.quote)); });
  exp.forEach(e => { const k = e.category || "Non-vendor"; byCat[k] = money((byCat[k] || 0) + num(e.amount)); });
  const categories = Object.entries(byCat).filter(([, a]) => a > 0).sort((x, y) => y[1] - x[1]);
  const expRows = exp.map(e => ({ id: e.id, label: e.label, amount: money(e.amount), due: e.due || null, paid: !!e.paid, paidAt: e.paidAt || null,
    status: e.paid ? "Paid" : (e.due && e.due < today ? "Overdue" : "Pending") }));
  const lineItems = active.map(v => {
    const vPaid = money((v.depositPaid ? num(v.deposit) : 0) + (v.finalPaid ? num(v.final) : 0));
    return { id: v.id, name: v.name, total: money(v.quote), paid: vPaid, balance: money(num(v.quote) - vPaid) };
  });
  const due = [...payments.filter(p => !p.paid).map(p => ({ label: p.label, due: p.due, amount: p.amount, status: p.status })),
    ...expRows.filter(e => !e.paid && e.due).map(e => ({ label: e.label, due: e.due, amount: e.amount, status: e.status }))]
    .sort((a, b) => String(a.due || "9999").localeCompare(String(b.due || "9999")));
  return {
    budget, hasBudget: budget > 0, committed, paid, vendorTotal, expTotal,
    remaining: budget > 0 ? money(budget - committed) : null,
    pct: budget > 0 ? Math.round(committed / budget * 100) : null,
    over: budget > 0 && committed > budget,
    categories, payments, expenses: expRows, lineItems, comingDue: due
  };
}

/* ───────────── Phase 13 · Batch B: category budgets and her fee ───────────── */

export const BUDGET_MODES = ["cap", "perGuest"];
const catKey = c => str(c, 60).toLowerCase();

/** Category budgets as stored in budgetsByClient: known modes, amounts ≥ 0, one row per category (max 40). */
export function normalizeBudgets(v) {
  const o = v && typeof v === "object" && !Array.isArray(v) ? v : {};
  const seen = new Set();
  const categories = [];
  (Array.isArray(o.categories) ? o.categories : []).forEach(x => {
    if (!x || categories.length >= 40) return;
    const category = str(x.category, 60);
    const k = category.toLowerCase();
    const amount = Math.max(0, money(num(x.amount)));
    if (!category || seen.has(k) || !(amount > 0)) return; // a $0 budget would flag anything booked
    seen.add(k);
    categories.push({ category, mode: x.mode === "perGuest" ? "perGuest" : "cap", amount });
  });
  return { categories, shareWithCouple: o.shareWithCouple !== false };
}

/**
 * Each category budget against what's booked: committed vendors' quotes in that category plus her other costs
 * filed under it. A per-guest budget needs the couple's guest count; without one its target is null (no flag).
 */
export function categoryTargets({ budgets, client, vendors, expenses }) {
  const b = normalizeBudgets(budgets);
  const guests = Math.max(0, Math.round(num(client && client.guests)));
  const cid = client && client.id;
  const active = (vendors || []).filter(v => v && v.client === cid && isCommitted(v));
  const exp = (expenses || []).filter(Boolean);
  return b.categories.map(r => {
    const k = r.category.toLowerCase();
    const booked = money(active.filter(v => catKey(v.category) === k).reduce((a, v) => a + num(v.quote), 0)
      + exp.filter(e => catKey(e.category) === k).reduce((a, e) => a + num(e.amount), 0));
    const target = r.mode === "perGuest" ? (guests > 0 ? money(r.amount * guests) : null) : r.amount;
    return { ...r, guests, target, booked, remaining: target == null ? null : money(target - booked), over: target != null && booked > target };
  });
}

/** The target row for a category (any case), or null. */
export const targetFor = (targets, category) => (targets || []).find(t => t.category.toLowerCase() === catKey(category)) || null;

/**
 * Phase 13 · Step 8: where a vendor's quote would put its category. `saved` is the vendor as stored (its quote may
 * already be in `booked`); `form` is what's on screen now. Null when the category has no budget, the vendor isn't
 * going ahead, or it stays within budget.
 */
export function overCap(targets, saved, form) {
  const t = targetFor(targets, form && form.category);
  if (!t || t.target == null || !form || form.contractStatus === "Rejected") return null;
  const counted = saved && isCommitted(saved) && catKey(saved.category) === t.category.toLowerCase() ? num(saved.quote) : 0;
  const total = money(t.booked - counted + Math.max(0, num(form.quote)));
  return total > t.target ? { category: t.category, total, target: t.target, over: money(total - t.target) } : null;
}

/** What the couple sees of the category budgets: the rows, only when she shares them. */
export function budgetsForCouple(budgets) {
  const b = normalizeBudgets(budgets);
  return b.shareWithCouple && b.categories.length ? { categories: b.categories } : null;
}

/** Her planning fee as the couple sees it: label, amount, due date, paid or not. No overdue wording, no paid dates. */
export function feesForCouple(fees) {
  return (Array.isArray(fees) ? fees : []).filter(f => f && Number(f.amount) > 0)
    .map(f => ({ label: str(f.label, 160), amount: money(f.amount), due: ymd(f.due) || "", paid: !!f.paidAt }));
}

/* ───────────── Timeline ───────────── */

/**
 * Planning milestones for her package, counted back from the wedding date (days before). The agreement's own
 * deadlines (Section 7) are included, at the days her Settings say. Amanda edits the list afterwards.
 */
export function milestoneTemplate(packageId, settings) {
  const t = normalizeSettings(settings).terms;
  const contract = [
    [60, "Vendor contracts, guest count and contacts due", "From the agreement: the couple sends copies of every vendor contract, the guest count and contact details.", "contract"],
    [60, "Day-of decision maker named", "From the agreement: one person who can make decisions on the day.", "contract"],
    [t.vendorMealDaysBefore, "Vendor meal count to the couple", "From the agreement: a plate count for every vendor working the event, before the final catering order.", "contract"],
    [t.balanceDaysBefore, "Balance of the planning fee due", "From the agreement: the second installment.", "contract"]
  ];
  const close = [
    [14, "Final vendor confirmations", "Arrival times, contacts and final details confirmed with every vendor."],
    [10, "Final timeline to vendors", "The wedding day timeline sent to every vendor and the wedding party."],
    [1, "Rehearsal", "Walk through the ceremony with the wedding party and officiant."],
    [0, "Wedding day", ""]
  ];
  let list;
  if (packageId === "wedding-day") {
    list = [
      [56, "My involvement begins", "Wedding Day Coordination starts: confirm the details and review the timeline."],
      [42, "Planning meeting", "Go through the vendor list, the timeline draft and the venue's rules."],
      [30, "Venue walkthrough", "Walk the site: layout, load-in, power, weather plan."],
      ...contract, ...close
    ];
  } else {
    list = [
      [300, "Venue booked", "Date and venue confirmed."],
      [270, "Key vendors booked", "Photographer, caterer and music."],
      ...(packageId === "full-service" ? [[240, "Design concept approved", "Palette, florals, rentals and the overall look."]] : []),
      [180, "Save-the-dates out", ""],
      [150, "Remaining vendors booked", "Florals, rentals, hair and makeup, transportation."],
      [90, "Menu tasting", "Lock the menu and service style."],
      [70, "Invitations out", ""],
      [45, "Venue walkthrough", "Walk the site: layout, load-in, power, weather plan."],
      ...contract, ...close
    ];
  }
  return list.map(([days, title, desc, source]) => ({ days, title, desc: desc || "", source: source || "template" }));
}

/**
 * Milestones dated from the wedding day. Ones already in the past on `today` are left out (and counted), since a
 * couple who books late doesn't need "Venue booked" a year ago. Rehearsal is left out for Wedding Day Coordination
 * unless they added the rehearsal.
 */
export function buildMilestones({ client, settings, today, now = Date.now() }) {
  const event = ymd(client && client.date);
  if (!event) return { items: [], skipped: 0, missingDate: true };
  const pkg = client.packageId || "partial";
  const rehearsal = pkg !== "wedding-day" || (client.addonIds || []).includes("rehearsal");
  let skipped = 0;
  const items = [];
  milestoneTemplate(pkg, settings).forEach((m, i) => {
    if (m.title === "Rehearsal" && !rehearsal) return;
    const date = addDays(event, -m.days);
    if (date < today && m.days > 0) { skipped++; return; }
    items.push({ id: "ms-" + now.toString(36) + "-" + i, date, title: m.title, desc: m.desc, done: false, source: m.source });
  });
  items.sort((a, b) => a.date.localeCompare(b.date));
  return { items, skipped, missingDate: false };
}

export const sortMilestones = list => (list || []).slice().sort((a, b) => String(a.date || "9999").localeCompare(String(b.date || "9999")));

/** "16:30" → "4:30 PM". Free text (an older "4:30 PM") is shown as it is. */
export function prettyTime(v) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || "").trim());
  if (!m) return String(v || "");
  const h = Number(m[1]), mm = m[2];
  return ((h % 12) || 12) + ":" + mm + " " + (h < 12 ? "AM" : "PM");
}
/** "4:30 PM" → "16:30" (for the time picker); anything else → "". */
export function timeValue(v) {
  const s = String(v || "").trim();
  if (/^\d{1,2}:\d{2}$/.test(s)) return s.padStart(5, "0");
  const m = /^(\d{1,2}):(\d{2})\s*([AaPp])\.?[Mm]\.?$/.exec(s);
  if (!m) return "";
  let h = Number(m[1]) % 12;
  if (/p/i.test(m[3])) h += 12;
  return String(h).padStart(2, "0") + ":" + m[2];
}
const timeKey = v => timeValue(v) || "99:99";

/** Phase 13 · Step 4: the two timelines' names, in one place. */
export const TIMELINE_LABELS = { day: "Wedding day timeline", planning: "Planning timeline", dayShort: "Wedding day", item: "wedding-day item" };

/* Phase 13 · Step 5: end times on the wedding day timeline. */
const toMin = v => { const t = timeValue(v); if (!t) return null; const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const fromMin = n => { const x = ((n % 1440) + 1440) % 1440; return String(Math.floor(x / 60)).padStart(2, "0") + ":" + String(x % 60).padStart(2, "0"); };
/** "16:30" + 30 → "17:00" (wraps past midnight). "" when the start isn't a time. */
export const addMinutes = (hhmm, n) => { const a = toMin(hhmm); return a == null ? "" : fromMin(a + Number(n || 0)); };
/** Minutes from start to end; an end before the start is read as the next day. null when either isn't a time. */
export function minutesBetween(start, end) {
  const a = toMin(start), b = toMin(end);
  if (a == null || b == null) return null;
  return b >= a ? b - a : b + 1440 - a;
}
/** "4:30 – 5:00 PM", or "11:45 AM – 12:15 PM" across noon. Just the start when there's no end. */
export function prettyRange(time, end) {
  const a = timeValue(time), b = timeValue(end);
  if (!a || !b || a === b) return prettyTime(time);
  const sa = prettyTime(a), sb = prettyTime(b);
  const half = x => x.slice(-2);
  return half(sa) === half(sb) ? sa.slice(0, -3) + " – " + sb : sa + " – " + sb;
}
/** 30 → "30 min", 75 → "1 hr 15 min", 120 → "2 hr". */
export function durationLabel(mins) {
  const n = Math.round(Number(mins));
  if (!(n > 0)) return "";
  const h = Math.floor(n / 60), m = n % 60;
  return h ? h + " hr" + (m ? " " + m + " min" : "") : m + " min";
}
export const sortRunOfShow = list => (list || []).slice().sort((a, b) => timeKey(a.time).localeCompare(timeKey(b.time)));

/** The wedding-day plan: the run of show, the venue's areas, and the weather backup notes. */
export function normalizeDayOf(v) {
  const o = v && typeof v === "object" && !Array.isArray(v) ? v : {};
  const lines = a => (Array.isArray(a) ? a : []).map(x => str(x, 300)).filter(Boolean);
  return {
    items: (Array.isArray(o.items) ? o.items : []).filter(Boolean).map((x, i) => ({
      id: str(x.id, 80) || "rs-" + i, time: str(x.time, 20), end: str(x.end, 20), event: str(x.event, 300), vendor: str(x.vendor, 160), zone: str(x.zone, 160), note: str(x.note, 500), person: str(x.person, 160)
    })),
    areas: lines(o.areas), backups: lines(o.backups)
  };
}

/* ───────────── What the couple sees (portal) ───────────── */

export function milestonesForCouple(list) {
  return sortMilestones((Array.isArray(list) ? list : []).filter(Boolean)).map(m => ({ date: m.date || "", title: str(m.title, 200), desc: str(m.desc, 500), done: !!m.done }));
}
export function dayOfForCouple(v) {
  const d = normalizeDayOf(v);
  return { items: sortRunOfShow(d.items).map(({ time, end, event, vendor, zone, note, person }) => ({ time, end, event, vendor, zone, note, person })), areas: d.areas, backups: d.backups };
}
export function expensesForCouple(list) {
  return (Array.isArray(list) ? list : []).filter(Boolean).map(e => ({ id: e.id, label: str(e.label, 160), category: str(e.category, 60), amount: money(e.amount), due: e.due || "", paid: !!e.paid }));
}

/* ───────────── The weekly report ───────────── */

/** Monday and Sunday of the week containing `today` (YYYY-MM-DD). */
export function weekBounds(today) {
  const [y, m, d] = String(today).split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d));
  const mon = addDays(today, -((t.getUTCDay() + 6) % 7));
  return { start: mon, end: addDays(mon, 6) };
}
const dayOfIso = iso => {
  if (!iso) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) return iso;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
};
const within = (day, b) => !!day && day >= b.start && day <= b.end;

/**
 * The week's real activity for one couple: tasks completed, vendor contacts logged, payments marked paid,
 * what's coming due in the next two weeks, and what she's still waiting on from the couple.
 */
export function weeklyDigest({ client, tasks, vendors, expenses, fees, milestones, today, horizonDays = 14 }) {
  const b = weekBounds(today);
  const until = addDays(today, horizonDays);
  const vs = (vendors || []).filter(v => v && v.client === (client && client.id));
  const tasksDone = (tasks || []).filter(t => t && t.status === "complete" && within(dayOfIso(t.completedAt), b)).map(t => t.title);
  const contacts = [];
  vs.forEach(v => (v.contacts || []).forEach(c => { const d = dayOfIso(c.at); if (within(d, b)) contacts.push({ name: v.name, day: d, note: c.note || "" }); }));
  contacts.sort((x, y) => x.day.localeCompare(y.day));
  const payments = [
    ...vendorPayments(vs, today).filter(p => p.paid && within(dayOfIso(p.paidAt), b)).map(p => ({ label: p.label, amount: p.amount })),
    ...(expenses || []).filter(e => e && e.paid && within(dayOfIso(e.paidAt), b)).map(e => ({ label: e.label, amount: money(e.amount) })),
    ...(fees || []).filter(f => f && f.paidAt && within(dayOfIso(f.paidAt), b)).map(f => ({ label: f.label.replace(/\s*\(.*\)$/, "") + " (planning fee)", amount: money(f.amount) }))
  ];
  const payTotal = money(payments.reduce((a, p) => a + p.amount, 0));
  const soon = d => !!d && d >= today && d <= until;
  const comingUp = [
    ...(milestones || []).filter(m => m && !m.done && soon(m.date)).map(m => ({ day: m.date, label: m.title })),
    ...vendorPayments(vs, today).filter(p => !p.paid && soon(p.due)).map(p => ({ day: p.due, label: p.label + " · " + usd(p.amount) })),
    ...(expenses || []).filter(e => e && !e.paid && soon(e.due)).map(e => ({ day: e.due, label: e.label + " · " + usd(e.amount) })),
    ...(fees || []).filter(f => f && !f.paidAt && soon(f.due)).map(f => ({ day: f.due, label: f.label + " · " + usd(f.amount) }))
  ].sort((x, y) => x.day.localeCompare(y.day));
  const needs = (tasks || []).filter(t => t && t.source === "contract" && t.owner === "couple" && t.status !== "complete")
    .map(t => ({ label: t.coupleTitle || t.title, due: t.due || "" }));
  return { week: b, tasksDone, contacts, payments, payTotal, comingUp, needs };
}
const usd = n => "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(Number(n)) ? 0 : 2, maximumFractionDigits: 2 });

/* ───────────── Couple uploads (portal) ───────────── */

export const UPLOAD_TYPES = { "application/pdf": "pdf", "image/jpeg": "jpg", "image/png": "png", "image/heic": "heic", "image/heif": "heic" };
const EXT_TYPE = { pdf: "application/pdf", jpg: "image/jpeg", jpeg: "image/jpeg", png: "image/png", heic: "image/heic", heif: "image/heif" };
export const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const UPLOADS_PER_DAY = 10;

/**
 * Checks a file a couple wants to send: PDF, JPG, PNG or HEIC (by type, or by extension when the phone gives no
 * type), at most 20 MB, at most 10 a day. Returns { type, ext, safeName } or throws an Error with .status.
 */
export function checkUpload({ name, type, size }, uploadsToday) {
  const err = (status, message) => { const e = new Error(message); e.status = status; return e; };
  const rawName = str(name, 200) || "file";
  const ext = (/\.([a-z0-9]+)$/i.exec(rawName) || [])[1];
  const t = String(type || "").toLowerCase();
  const mime = UPLOAD_TYPES[t] ? t : (ext && EXT_TYPE[ext.toLowerCase()]) || "";
  if (!mime || (t && !UPLOAD_TYPES[t] && t !== "application/octet-stream")) throw err(415, "I can take PDFs and photos (JPG, PNG or HEIC). Save it as one of those and try again.");
  const n = Number(size);
  if (!(n > 0)) throw err(400, "That file looks empty. Try choosing it again.");
  if (n > UPLOAD_MAX_BYTES) throw err(413, "That file is over 20 MB. Try a smaller copy, or email it to me instead.");
  if (Number(uploadsToday || 0) >= UPLOADS_PER_DAY) throw err(429, "That's ten files today, which is the daily limit. Send the rest tomorrow, or email them to me.");
  const safeName = rawName.replace(/[\\/:*?"<>|#%\u0000-\u001f]+/g, "_").replace(/\.{2,}/g, ".").replace(/\s+/g, " ").slice(-120);
  return { type: mime, ext: UPLOAD_TYPES[mime], safeName };
}

/** The Documents entry for a couple's upload. */
export function coupleUploadDoc({ file, note, now }) {
  const day = new Date(now || Date.now()).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return { id: "upload-" + Date.parse(now || new Date().toISOString()).toString(36) + Math.random().toString(36).slice(2, 5),
    name: str(file.name.replace(/\.[^.]+$/, ""), 160) || "File from the couple", category: "From the couple", vendor: null, status: "Received",
    range: "Received " + day, amount: 0, terms: str(note, 500), cancel: "", insurance: "", file, fromCouple: true, receivedAt: now || new Date().toISOString() };
}

/* ───────────── Removed couples (30-day restore) ───────────── */

export const RESTORE_DAYS = 30;
/** Entries older than 30 days are purged; the rest can be restored. */
export function restorable(removed, today) {
  return Object.values(removed || {}).filter(Boolean).filter(r => r.removedAt && daysBetween(dayOfIso(r.removedAt), today) <= RESTORE_DAYS)
    .sort((a, b) => String(b.removedAt).localeCompare(String(a.removedAt)));
}
export function expiredRemovals(removed, today) {
  return Object.values(removed || {}).filter(Boolean).filter(r => !r.removedAt || daysBetween(dayOfIso(r.removedAt), today) > RESTORE_DAYS);
}
/** Typed-name confirmation: case, spacing and the "&"/"and" choice don't matter. */
export function namesMatch(typed, names) {
  const n = s => String(s || "").toLowerCase().replace(/\band\b/g, "&").replace(/[^a-z0-9&]+/g, "");
  return !!n(names) && n(typed) === n(names);
}

/* ───────────── Bridal Party & Important People (Batch C, Step 12) ─────────────
   One list per couple. Amanda edits it on the People tab; the couple edits it from their portal ("Your people").
   Vendors never see it. */

export const PEOPLE_ROLES = ["Maid/Matron of honor", "Best man", "Bridesmaid", "Groomsman", "Flower girl", "Ring bearer", "Parent", "Grandparent", "Officiant", "Day-of contact", "VIP", "Other"];
export const PEOPLE_SIDES = ["Partner 1's side", "Partner 2's side", "Both"];
export const PEOPLE_MAX = 80;

/** Cleans a people list from either side (dashboard or portal): known roles, trimmed fields, at most 80 people. */
export function normalizePeople(list, now = Date.now()) {
  const seen = new Set();
  return (Array.isArray(list) ? list : []).filter(p => p && str(p.name, 120)).slice(0, PEOPLE_MAX).map((p, i) => ({
    id: /^[A-Za-z0-9_-]{1,40}$/.test(String(p.id || "")) && !seen.has(String(p.id)) && seen.add(String(p.id)) ? String(p.id) : "pp-" + now.toString(36) + "-" + i,
    name: str(p.name, 120), role: PEOPLE_ROLES.includes(p.role) ? p.role : "Other",
    side: PEOPLE_SIDES.includes(p.side) ? p.side : "", phone: str(p.phone, 40), email: str(p.email, 160), notes: str(p.notes, 600),
    addedBy: p.addedBy === "couple" ? "couple" : "planner"
  }));
}

/** People in role order (honor attendants first), then by name. */
export function sortPeople(list) {
  const rank = r => { const i = PEOPLE_ROLES.indexOf(r); return i < 0 ? 99 : i; };
  return (list || []).slice().sort((a, b) => rank(a.role) - rank(b.role) || a.name.localeCompare(b.name));
}
